import type { AutomationRunDto } from '@tms/shared';
import type { Locale } from '../../../i18n';

/**
 * Runs → a CSV file (TestRun src/utils/export.ts wrote an HTML table named
 * .xls; TMS has no spreadsheet writer, and a CSV opens in Excel, Numbers and
 * LibreOffice alike). The separator follows the language, as Excel does: a
 * Russian Excel splits columns on «;», an English one on «,». The BOM makes
 * Excel read the file as UTF-8.
 */

const HEADERS = [
  'ID',
  'User',
  'Pipeline',
  'Env',
  'Status',
  'Passed',
  'Failed',
  'Broken',
  'Skipped',
  'Duration',
  'Time',
  'Allure',
];

const cell = (value: string | number | undefined | null, separator: string): string => {
  const text = value === undefined || value === null ? '' : String(value);
  return /["\r\n]/.test(text) || text.includes(separator) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const runsToCsv = (runs: AutomationRunDto[], locale: Locale): string => {
  const separator = locale === 'ru' ? ';' : ',';
  const rows = runs.map((run) =>
    [
      run.id,
      run.user,
      run.pipelineId,
      run.config.environment,
      run.status,
      run.result?.passed,
      run.result?.failed,
      run.result?.broken,
      run.result?.skipped,
      run.result?.duration,
      run.createdAt,
      run.allureUrl,
    ]
      .map((value) => cell(value, separator))
      .join(separator),
  );
  return [HEADERS.join(separator), ...rows].join('\r\n');
};

/** Hands the runs to the browser as test-runs-<date>.csv. */
export const downloadRunsCsv = (runs: AutomationRunDto[], locale: Locale): void => {
  const blob = new Blob(['﻿', runsToCsv(runs, locale)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `test-runs-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
